"use client";

import * as React from "react";
import { Crown, ShieldCheck, Check, BarChart3, PiggyBank, Target, X } from "lucide-react";
import type { SignupConfig, PlanCard } from "@/components/signup/signup-content";

type Plan = "monthly" | "yearly" | "standard" | "premium";

function PlanCardView({ plan, selected, onSelect }: { plan: PlanCard; selected: boolean; onSelect: () => void }) {
  return (
    <div
      onClick={onSelect}
      className={`relative rounded-2xl border p-5 cursor-pointer transition-colors flex flex-col ${
        selected ? "border-leaf-400 ring-2 ring-leaf-300 bg-leaf-50/40" : "border-lavender-200 hover:border-cj-300"
      }`}
    >
      {plan.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-leaf-500 text-white text-[11px] font-bold whitespace-nowrap">
          ★ {plan.badge}
        </span>
      )}
      <div className="flex items-start gap-3">
        <input type="radio" name="cj-plan" checked={selected} onChange={onSelect} className="mt-1.5 w-4 h-4 accent-cj-800 shrink-0" />
        <span className="w-11 h-11 rounded-full bg-cj-50 flex items-center justify-center shrink-0">
          {plan.value === "yearly" || plan.value === "premium" ? <Crown className="w-6 h-6 text-leaf-600" /> : <BarChart3 className="w-6 h-6 text-cj-800" />}
        </span>
        <div>
          <p className="text-sm font-bold text-navy-500">{plan.title}</p>
          {plan.subtitle && <p className="text-[12px] text-gray-500">{plan.subtitle}</p>}
          <p className="text-cj-800">
            {/* A year at the monthly rate, struck through beside the offer price (client's plans mockup). */}
            {plan.strike && <span className="mr-2 text-[15px] font-bold text-gray-400 line-through">{plan.strike}</span>}
            <span className="text-2xl font-extrabold">{plan.price}</span>
            <span className="text-[13px] text-gray-500 font-medium"> {plan.per}</span>
          </p>
        </div>
      </div>

      {plan.save && (
        <span className="self-start mt-3 px-2.5 py-1 rounded-md bg-yellow-100 text-yellow-800 text-[12px] font-bold">{plan.save}</span>
      )}

      <ul className="space-y-1.5 mt-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] text-gray-600">
            <Check className="w-4 h-4 text-leaf-600 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onSelect}
        className={`mt-4 w-full h-10 rounded-lg text-sm font-bold transition-colors ${
          selected ? "bg-leaf-500 text-white hover:bg-leaf-600" : "border border-cj-300 text-cj-800 hover:bg-cj-50"
        }`}
      >
        {plan.cta}
      </button>
    </div>
  );
}

/**
 * Pricing popup over the locked dashboard. Closeable (the dashboard stays locked
 * as a teaser); choosing a plan advances to the Identity & Consent popup.
 */
export function PricingModal({
  config,
  selected,
  onSelect,
  onChoose,
  onClose,
}: {
  config: SignupConfig;
  selected: Plan;
  onSelect: (p: Plan) => void;
  onChoose: (p: Plan) => void;
  onClose: () => void;
}) {
  const stats = config.membershipStats;
  // The fee tile showed a STATIC annual figure ("$149.99 / per year · no auto-renewal") whatever
  // plan was selected. With a recurring plan in the list that is doubly wrong: the wrong price AND
  // a false auto-renewal claim on the screen that takes the card. Derive both from the chosen card.
  const chosen = config.plans.find((p) => p.value === selected);
  const isRecurringPlan = chosen?.value === "monthly";
  const feeLabel = chosen?.price ?? stats.fee;
  const perLabel = chosen
    ? `${chosen.per} · ${isRecurringPlan ? "renews automatically" : "no auto-renewal"}`
    : stats.per;

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl my-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div className="flex items-start gap-3">
            <span className="hidden sm:flex w-12 h-12 shrink-0 rounded-full bg-leaf-100 items-center justify-center">
              <Crown className="w-6 h-6 text-leaf-600" />
            </span>
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-cj-800 leading-tight">Unlock Your CJ Owl™ Membership</h2>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">{config.step3Intro}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1 text-gray-400 hover:text-navy-500 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 30 Days Money-back Guarantee banner */}
          <div className="flex items-center gap-4 rounded-xl border border-leaf-200 bg-leaf-50/50 p-4">
            <span className="w-12 h-12 rounded-full bg-leaf-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-7 h-7 text-leaf-600" />
            </span>
            <div>
              <p className="text-sm font-extrabold text-leaf-700">30 DAYS MONEY-BACK GUARANTEE</p>
              <p className="text-[12.5px] text-gray-600 leading-relaxed">
                Try CJ Owl risk-free. If you&apos;re not completely satisfied within 30 days of your annual plan, we&apos;ll
                refund 100% of your membership fee.
                {/* The guarantee is ANNUAL-ONLY (client, 2026-08-28). This banner sits above BOTH plans,
                    so without this line it promises the refund to a monthly buyer too. */}
                <span className="block mt-1 text-[11px] text-gray-400">Annual plan only. Terms &amp; Conditions apply.</span>
              </p>
            </div>
            <span className="hidden sm:block text-[11px] font-bold text-leaf-600 text-center shrink-0">RISK-FREE<br />MEMBERSHIP</span>
          </div>

          {/* Plans */}
          <div className="grid sm:grid-cols-2 gap-4">
            {config.plans.map((p) => (
              <PlanCardView key={p.value} plan={p} selected={selected === p.value} onSelect={() => onSelect(p.value)} />
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-xl bg-lavender-50 border border-lavender-200 p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-leaf-600 shrink-0" />
              <div>
                <p className="text-[11px] text-gray-500">Membership Fee</p>
                <p className="text-base font-extrabold text-cj-800">{feeLabel}</p>
                <p className="text-[11px] text-gray-400">{perLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <PiggyBank className="w-7 h-7 text-leaf-600 shrink-0" />
              <div>
                <p className="text-[11px] text-gray-500">Average {config.audience} Savings</p>
                <p className="text-base font-extrabold text-leaf-600">{stats.savings}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Target className="w-7 h-7 text-leaf-600 shrink-0" />
              <div>
                <p className="text-[11px] text-gray-500">Potential ROI</p>
                <p className="text-base font-extrabold text-cj-800">{stats.roi}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row-reverse gap-3 pt-1">
            <button
              type="button"
              onClick={() => onChoose(selected)}
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-lg bg-leaf-500 hover:bg-leaf-600 text-white text-sm font-bold transition-colors sm:flex-1"
            >
              Continue with {config.plans.find((p) => p.value === selected)?.title ?? "Membership"} Plan
            </button>
            {/* The free plan is a CHOICE, not a way out of a popup. "I'll decide later" read as a
                dismissal, so a member who meant to stay on Basic could not tell they had picked
                anything (client, 2026-11-03: "upgrade to premium or stay on free plan for now").
                Same action, named for what it does. */}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-lavender-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition-colors"
            >
              Stay on the free plan for now
            </button>
          </div>
          <p className="text-center text-[12px] text-gray-400">
            {/* "No Auto-Renewal" was removed on 2026-08-28: Premium Monthly recurs, and this
                modal shows it, so the claim was false for the plan directly above it. */}
            🔒 Secure &amp; Encrypted&nbsp;&nbsp;•&nbsp;&nbsp;No Obligation
          </p>
        </div>
      </div>
    </div>
  );
}
